// src/components/dashboard/user-requests/hooks/useUserRequests.ts
import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useFamilyStore } from '@/store/familyStore';
import { AddChildRequestData, AddInfoRequestData } from '../types/request';
import { transformChildRequests, transformInfoRequests } from '../utils/requestTransformers';
import { createChildRequest, createInfoRequest } from '../utils/requestCreators';

export function useUserRequests() {
  const user = useAuthStore(state => state.user);
  const { 
    addRequest, 
    requests: allRequests = [], 
    personalInfoRequests = [], 
    addPersonalInfoRequest 
  } = useFamilyStore();

  const requests = useMemo(() => {
    if (!user) return [];

    const childRequests = transformChildRequests(allRequests, user.id);
    const infoRequests = transformInfoRequests(personalInfoRequests, user.id);

    return [...childRequests, ...infoRequests]
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [user, allRequests, personalInfoRequests]);

  const handleAddChildRequest = (data: AddChildRequestData) => {
    if (!user) return;
    const request = createChildRequest(user.id, user.username, data);
    addRequest(request);
  };

  const handleAddInfoRequest = (data: AddInfoRequestData) => {
    if (!user) return;
    const request = createInfoRequest(user.id, user.username, user.phoneNumber, data);
    addPersonalInfoRequest(request);
  };

  return {
    requests,
    handleAddChildRequest,
    handleAddInfoRequest
  };
}
